import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { createV2BulkTransactions, previewCryptoCsvTransactions } from '../../composables/fetchAPI';
import { formatCryptoPrice, formatCryptoQty } from '../../utils/cryptoDecimal';
import { CryptoCsvRowError, prepareCryptoCsv } from '../../utils/cryptoCsv';
import { showApiErrorToast } from '../../utils/apiErrorToast';

// นำเข้า CSV สำหรับพอร์ตคริปโต (ตามคู่มือ Crypto_Portfolio_V2_CSV_Import_Guide)
// 0) ผู้ใช้กรอกได้แค่ BUY / SELL — หน้าเว็บคำนวณเงินสดที่ต้องใช้ แล้วแทรก OPENING_CASH เป็นรายการแรก
//    (หลังบ้านไม่มี AUTO_OPENING_CASH สำหรับคริปโต) จากนั้นส่งไฟล์ที่เตรียมแล้วแทนไฟล์ต้นฉบับ
// 1) อัปโหลดไฟล์ → POST .../transactions/import/csv (file, subPortId, autoCashMode=NONE) ได้ PREVIEW
// 2) ผู้ใช้ยืนยัน → POST .../transactions/bulk { source: 'CSV_IMPORT_PREVIEW', items: preview.items }
//    ส่ง items ตามที่หลังบ้านคืนมาตรงตัว (decimalValues เป็น string + idempotencyKey) ห้ามแก้ค่า

interface CryptoCSVImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  portfolioId: string;
  subPortId: string;
  subPortName?: string;
  onSave: () => void;
}

const MAX_FILE_BYTES = 5 * 1024 * 1024; // controller จำกัด 5 MiB
const TEMPLATE_HEADER = 'date,time,action,ticker,qty,price,fee,tax,currency,amount,cost,remark';
const TEMPLATE_CSV = [
  TEMPLATE_HEADER,
  '2026-10-02,09:00:00,BUY,BTC,0.01,62000,1.5,0,USD,,,First BTC',
  '2026-10-03,14:30:00,BUY,ETH,0.5,2400,1.2,0,USD,,,First ETH',
  '2026-10-05,03:15:00,BUY,SOL,10,145.25,0.8,0,USD,,,First SOL',
  '2026-10-08,10:00:00,BUY,BTC,0.005,64500,0.9,0,USD,,,Add BTC',
  '2026-10-10,16:45:00,SELL,ETH,0.2,2550,0.6,0,USD,,,Take profit ETH',
  '2026-10-12,08:20:00,BUY,SOL,5.5,138.4,0.5,0,USD,,,Buy the dip SOL',
  '2026-10-15,21:05:00,SELL,BTC,0.004,66800,0.8,0,USD,,,Partial sell BTC',
  '2026-10-18,11:30:00,BUY,ETH,0.35,2480.75,0.9,0,USD,,,Add ETH',
  '2026-10-21,05:50:00,SELL,SOL,8,152.1,0.7,0,USD,,,Partial sell SOL',
  '2026-10-24,13:10:00,BUY,BTC,0.0025,65250.5,0.6,0,USD,,,Add BTC',
].join('\n');

const ACTIONS: { action: string; label: string; fields: string }[] = [
  { action: 'BUY', label: 'ซื้อเหรียญ', fields: 'ticker, qty, price' },
  { action: 'SELL', label: 'ขายเหรียญ', fields: 'ticker, qty, price' },
];

const ACTION_COLORS: Record<string, string> = {
  BUY: 'text-green-400',
  OPENING_CASH: 'text-yellow-300',
  SELL: 'text-red-400',
  OPENING_POSITION: 'text-violet-300',
};

const pick = (obj: any, ...keys: string[]) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
};

// อ่านค่าจาก decimalValues ก่อน (string ตรงตัว) แล้วค่อย fallback field ปกติ
const dv = (item: any, key: string) => pick(item?.decimalValues, key) ?? pick(item, key);

const formatDateTime = (value: any) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  // แสดงเป็นเวลา UTC ให้ตรงกับไฟล์ (ไฟล์คริปโตใช้ UTC)
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 19)} UTC`;
};

// error จาก handleApiError มี th_message / traceId / details แนบมา
const errorText = (err: any) =>
  typeof err === 'string' ? err : err?.thMessage || err?.message || err?.code || JSON.stringify(err);

const CryptoCSVImportModal: React.FC<CryptoCSVImportModalProps> = ({ isOpen, onClose, portfolioId, subPortId, subPortName, onSave }) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [localErrors, setLocalErrors] = useState<CryptoCsvRowError[]>([]);
  const [opening, setOpening] = useState<{ amount: string; date: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPreview(null);
    setLocalErrors([]);
    setOpening(null);
  }, [file]);

  if (!isOpen) return null;

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV + '\n'], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'crypto_portfolio_v2_example.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePreview = async () => {
    if (!file) return toast.error('กรุณาเลือกไฟล์ CSV');
    if (!subPortId) return toast.error('กรุณาเลือกพอร์ตย่อยก่อนนำเข้า');
    if (!/\.csv$/i.test(file.name)) return toast.error('รองรับเฉพาะไฟล์ .csv');
    if (file.size > MAX_FILE_BYTES) return toast.error('ไฟล์ใหญ่เกิน 5 MiB');

    // ตรวจแถว BUY/SELL + คำนวณ OPENING_CASH ฝั่งหน้าเว็บก่อน (ผิดตรงนี้ไม่ต้องยิงหลังบ้าน)
    const prepared = prepareCryptoCsv(await file.text());
    setPreview(null);
    setLocalErrors(prepared.errors);
    setOpening(prepared.openingCash && prepared.openingDate ? { amount: prepared.openingCash, date: prepared.openingDate } : null);
    if (prepared.errors.length) {
      setShowGuide(false);
      return toast.error(`พบข้อผิดพลาดในไฟล์ ${prepared.errors.length} จุด`);
    }

    setLoading(true);
    try {
      // ส่งไฟล์ที่เตรียมแล้ว (มี OPENING_CASH เป็นแถวแรก) แทนไฟล์ต้นฉบับ
      const uploadFile = new File([prepared.csvText], file.name, { type: 'text/csv' });
      const res = await previewCryptoCsvTransactions(portfolioId, uploadFile, subPortId);
      const data = res?.data ?? res;
      setPreview(data);
      setShowGuide(false);
      const errCount = Number(data?.errorCount ?? data?.errors?.length ?? 0);
      if (errCount > 0) toast.error(`พบข้อผิดพลาด ${errCount} รายการ`);
      else toast.success(`ตรวจสอบผ่าน ${data?.items?.length ?? 0} รายการ`);
    } catch (e: any) {
      showApiErrorToast(e, 'ตรวจสอบไฟล์ไม่สำเร็จ');
      console.error('Crypto CSV preview error:', e);
    } finally {
      setLoading(false);
    }
  };

  const items: any[] = Array.isArray(preview?.items) ? preview.items : [];
  const errors: any[] = Array.isArray(preview?.errors) ? preview.errors : [];
  const warnings: any[] = Array.isArray(preview?.warnings) ? preview.warnings : [];
  const errorCount = Number(preview?.errorCount ?? errors.length);
  const canCommit = !!preview && items.length > 0 && errorCount === 0 && !committing;

  const handleCommit = async () => {
    if (!canCommit) return;
    setCommitting(true);
    try {
      await createV2BulkTransactions(portfolioId, items, undefined, 'CSV_IMPORT_PREVIEW');
      toast.success(`นำเข้าสำเร็จ ${items.length} รายการ`);
      onSave();
      onClose();
    } catch (e: any) {
      showApiErrorToast(e, 'นำเข้าไม่สำเร็จ');
      console.error('Crypto CSV commit error:', e);
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="crypto-port-card w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-white/10">
          <div>
            <div className="text-[11px] font-bold tracking-widest text-orange-400">PORTFOLIO V2 / CRYPTO · BETA</div>
            <h2 className="text-xl font-bold" style={{ color: 'var(--text-color)' }}>นำเข้า CSV พอร์ตคริปโต</h2>
            <p className="text-xs text-gray-400 mt-1">
              พอร์ตย่อย: <span className="font-bold" style={{ color: 'var(--yellow-text-color)' }}>{subPortName || subPortId || '-'}</span>
              {' '}· USD เท่านั้น · เวลาในไฟล์เป็น UTC
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl leading-none" aria-label="ปิด">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4">
          {/* คู่มือย่อ */}
          <details open={showGuide} onToggle={(e) => setShowGuide(e.currentTarget.open)} className="rounded-lg border border-white/10 bg-black/20">
            <summary className="cursor-pointer select-none px-4 py-2 text-sm font-bold" style={{ color: 'var(--yellow-text-color)' }}>
              รูปแบบไฟล์ที่รองรับ
            </summary>
            <div className="px-4 pb-4 text-xs text-gray-300 flex flex-col gap-3">
              <div>
                <div className="font-bold text-gray-200 mb-1">หัวคอลัมน์ (UTF-8)</div>
                <code className="block bg-black/40 rounded p-2 text-[11px] break-all text-orange-200">{TEMPLATE_HEADER}</code>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {ACTIONS.map(a => (
                  <div key={a.action} className="flex items-baseline gap-2 bg-black/20 rounded px-2 py-1">
                    <code className="font-bold text-violet-300 text-[11px]">{a.action}</code>
                    <span className="text-gray-300">{a.label}</span>
                    <span className="ml-auto text-gray-500 text-[10px]">{a.fields}</span>
                  </div>
                ))}
              </div>
              <ul className="list-disc pl-5 space-y-0.5 text-gray-400">
                <li>ไฟล์ไม่เกิน 5 MiB · 1–10,000 แถว · ทศนิยมสูงสุด 36 ตำแหน่ง</li>
                <li>กรอกเฉพาะรายการ <b className="text-gray-200">BUY / SELL</b> · คอลัมน์ amount / cost เว้นว่างไว้</li>
                {/* <li>ไม่ต้องใส่เงินสด ระบบคำนวณเงินที่ต้องใช้แล้วเพิ่ม <b className="text-yellow-300">OPENING_CASH</b> เป็นรายการแรกให้อัตโนมัติ</li> */}
                <li>ticker ใช้ได้ทั้ง BTC, BTC/USD หรือ X:BTCUSD </li>
                <li>fee / tax ไม่ใส่ถือเป็น 0 · amount, cost, remark ไม่ใส่ก็ได้ </li>
              </ul>
              <button onClick={downloadTemplate} className="self-start text-xs font-bold px-3 py-1.5 rounded border border-orange-400/50 text-orange-300 hover:bg-orange-500/10">
                ⬇ ดาวน์โหลดไฟล์ตัวอย่าง
              </button>
            </div>
          </details>

          {/* เลือกไฟล์ */}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 text-left px-4 py-3 rounded-lg border border-dashed border-white/20 hover:border-orange-400/60 bg-black/20 text-sm"
              style={{ color: 'var(--text-color)' }}
            >
              {file ? (
                <span>📄 <b>{file.name}</b> <span className="text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span></span>
              ) : (
                <span className="text-gray-400">คลิกเพื่อเลือกไฟล์ .csv</span>
              )}
            </button>
            <button
              onClick={handlePreview}
              disabled={!file || loading}
              className="px-5 py-3 rounded-lg font-bold text-sm text-[#18181b] bg-yellow-400 hover:bg-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'กำลังตรวจสอบ...' : 'ตรวจสอบไฟล์'}
            </button>
          </div>

          {/* ข้อผิดพลาดจากการตรวจฝั่งหน้าเว็บ */}
          {localErrors.length > 0 && (
            <div className="p-3 bg-red-950/40 border border-red-500/50 rounded-lg max-h-56 overflow-y-auto">
              <p className="text-xs font-bold text-red-400 mb-2">พบข้อผิดพลาดในไฟล์ ({localErrors.length} จุด) — แก้ไฟล์แล้วตรวจสอบใหม่</p>
              <ul className="space-y-1.5 text-[11px] text-red-200">
                {localErrors.map((err, idx) => (
                  <li key={idx} className="flex items-start gap-1.5 bg-red-900/20 p-1.5 rounded border border-red-500/20">
                    <span className="bg-red-600 text-white px-1.5 py-0.5 rounded text-[9px] font-bold shrink-0">แถวที่ {err.row}</span>
                    <div>
                      {err.field && <span className="font-semibold text-yellow-300 uppercase">[{err.field}]</span>} {err.message}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* เงินสดตั้งต้นที่ระบบคำนวณให้ */}
          {opening && localErrors.length === 0 && (
            <div className="flex items-center gap-3 p-3 rounded-lg border border-yellow-400/40 bg-yellow-400/5">
              <span className="text-2xl" aria-hidden="true">💵</span>
              <div className="text-xs text-gray-300">
                <div>
                  เงินสดตั้งต้นที่ต้องใช้:{' '}
                  <b className="text-base" style={{ color: 'var(--yellow-text-color)' }}>${formatCryptoPrice(opening.amount)}</b>
                </div>
                <div className="text-gray-400">
                  ระบบเพิ่ม OPENING_CASH วันที่ {formatDateTime(opening.date)} (ก่อนรายการแรก 1 วัน) เป็นรายการแรกให้อัตโนมัติ
                </div>
              </div>
            </div>
          )}

          {/* ผล preview */}
          {preview && (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-2 text-[11px]">
                {preview.mode && <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-gray-300">mode: {preview.mode}</span>}
                {preview.format && <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-gray-300">format: {preview.format}</span>}
                {preview.timezone && <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-gray-300">timezone: {preview.timezone}</span>}
                <span className="px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/30 text-green-300">รายการ: {items.length}</span>
                <span className={`px-2 py-0.5 rounded-full border ${errorCount > 0 ? 'bg-red-500/10 border-red-500/30 text-red-300' : 'bg-white/5 border-white/10 text-gray-300'}`}>ข้อผิดพลาด: {errorCount}</span>
                {warnings.length > 0 && <span className="px-2 py-0.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-300">แจ้งเตือน: {warnings.length}</span>}
              </div>

              {errors.length > 0 && (
                <div className="p-3 bg-red-950/40 border border-red-500/50 rounded-lg max-h-48 overflow-y-auto">
                  <p className="text-xs font-bold text-red-400 mb-2">พบข้อผิดพลาดในไฟล์ ({errors.length} รายการ) — แก้ไฟล์แล้วตรวจสอบใหม่</p>
                  <ul className="space-y-1.5 text-[11px] text-red-200">
                    {errors.map((err, idx) => (
                      <li key={idx} className="flex items-start gap-1.5 bg-red-900/20 p-1.5 rounded border border-red-500/20">
                        {(err?.rowNo || err?.row) && (
                          <span className="bg-red-600 text-white px-1.5 py-0.5 rounded text-[9px] font-bold shrink-0">แถวที่ {err.rowNo || err.row}</span>
                        )}
                        <div>
                          {err?.field && <span className="font-semibold text-yellow-300 uppercase">[{err.field}]</span>} {errorText(err)}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {warnings.length > 0 && (
                <div className="p-3 bg-orange-950/30 border border-orange-500/40 rounded-lg max-h-32 overflow-y-auto">
                  <ul className="space-y-1 text-[11px] text-orange-200">
                    {warnings.map((w, idx) => (
                      <li key={idx}>{(w?.rowNo || w?.row) ? `แถวที่ ${w.rowNo || w.row}: ` : ''}{errorText(w)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {items.length > 0 && (
                <div className="overflow-x-auto max-h-72 overflow-y-auto border border-white/10 rounded-lg">
                  <table className="w-full text-xs text-left text-gray-300">
                    <thead className="bg-gray-900 sticky top-0 text-gray-400">
                      <tr>
                        <th className="p-2">#</th>
                        <th className="p-2">วันที่ (UTC)</th>
                        <th className="p-2">ประเภท</th>
                        <th className="p-2">เหรียญ</th>
                        <th className="p-2 text-right">จำนวน</th>
                        <th className="p-2 text-right">ราคา</th>
                        <th className="p-2 text-right">Fee / Tax</th>
                        <th className="p-2 text-right">เงินสด</th>
                        <th className="p-2">หมายเหตุ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => {
                        const type = String(pick(item, 'type', 'action') || '-').toUpperCase();
                        const qty = dv(item, 'quantity');
                        const price = dv(item, 'price');
                        const fee = dv(item, 'fee');
                        const tax = dv(item, 'tax');
                        const amount = dv(item, 'amount');
                        const symbol = pick(item, 'displaySymbol', 'assetSymbol', 'ticker', 'symbol');
                        return (
                          <tr key={item.idempotencyKey || idx} className="border-t border-white/5 hover:bg-white/5">
                            <td className="p-2 text-gray-500">{pick(item, 'rowNo', 'row') ?? idx + 1}</td>
                            <td className="p-2 whitespace-nowrap">{formatDateTime(pick(item, 'transactionDate', 'date'))}</td>
                            <td className={`p-2 font-bold ${ACTION_COLORS[type] || 'text-sky-300'}`}>{type}</td>
                            <td className="p-2 font-bold">{symbol ? String(symbol).replace(/^X:/i, '') : '-'}</td>
                            <td className="p-2 text-right tabular-nums">{qty !== undefined ? formatCryptoQty(qty) : '-'}</td>
                            <td className="p-2 text-right tabular-nums">{price !== undefined ? `$${formatCryptoPrice(price)}` : '-'}</td>
                            <td className="p-2 text-right tabular-nums text-gray-400">
                              {fee !== undefined || tax !== undefined ? `$${formatCryptoPrice(fee ?? '0')} / $${formatCryptoPrice(tax ?? '0')}` : '-'}
                            </td>
                            <td className="p-2 text-right tabular-nums">{amount !== undefined ? `$${formatCryptoPrice(amount)}` : '-'}</td>
                            <td className="p-2 text-gray-400 max-w-[160px] truncate" title={pick(item, 'note', 'remark')}>{pick(item, 'note', 'remark') || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-white/10">
          <span className="text-[11px] text-gray-500">ยืนยันแล้วจะบันทึกตามผลตรวจสอบนี้ทันที · ไฟล์เดิม/แถวเดิมถูกกันนำเข้าซ้ำ</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg font-bold text-sm text-gray-300 hover:text-white">ยกเลิก</button>
            <button
              onClick={handleCommit}
              disabled={!canCommit}
              className="px-5 py-2 rounded-lg font-bold text-sm text-white bg-gradient-to-r from-orange-500 to-violet-500 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {committing ? 'กำลังนำเข้า...' : `ยืนยันนำเข้า (${items.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CryptoCSVImportModal;
